<template>
  <div>
    <BaseListPage
        :name="name"
        :base-query="baseQuery"
        :store-path="storePath"
        :response-path="responsePath"
        :save-key="saveKey"
        :table-set-list="_tableSetList"
        :table-data-list="tableDataList"
        :async-render="true"
        title="商品一覧"
        icon="shopping-cart"
        table-class="row-clickable-table"
        @dataConvert="dataConvert"
        @row-click="onRowClick">
      <span slot="headerAction"
          class="action_button">
        <el-dropdown
            @command="actionCommand">
          <el-button size="small">
            アクション
            <i class="el-icon-arrow-down el-icon--right" />
          </el-button>
          <el-dropdown-menu
              slot="dropdown"
              class="header-action-menu">
            <el-dropdown-item
                :disabled="$isFunctionDisabled(define.PermissionKeys.Product.Detail.NEW)"
                :command="ACTION_MODE.NEW_DATA">
              新規登録
            </el-dropdown-item>
            <el-dropdown-item :command="ACTION_MODE.DISPLAY">
              表示項目設定
            </el-dropdown-item>
            <ToDownload
                page-key="product_list"
                :conditions="conditions"
                :keyword="searchKeyword"
                :is-and-search="isAndSearch" />
          </el-dropdown-menu>
        </el-dropdown>
      </span>
      <template
          slot="prefixTable">
        <div class="prefixTableRow">
          <el-button size="small"
              @click="clickDisplaySelect">
            表示項目
          </el-button>
          <el-button size="small"
              @click="clickDisplaySave">
            表示項目保存
          </el-button>
        </div>
      </template>
    </BaseListPage>
    <!-- 表示項目設定 -->
    <BaseDisplayListModal
        function-key="product_list"
        :display-config="displayItems"
        :store-path="storePath"
        :visible.sync="isDisplayListDialog" />
    <!-- 表示項目 -->
    <BaseDisplaySelectModal
        function-key="product_list"
        :table-set-list="_tableSetList"
        :display-config="displayItems"
        :store-path="storePath"
        :visible.sync="isDisplaySelectDialog" />
    <!-- 表示項目保存 -->
    <BaseDisplaySaveModal
        function-key="product_list"
        :display-config="displayItems"
        :visible.sync="isDisplaySaveDialog" />
  </div>
</template>

<style scoped>
.prefixTableRow .el-button {
  text-align: center;
  margin-bottom: 10px;
}
.prefixTableRow .el-button ::v-deep span {
  margin: 0px;
}

.base-list-page ::v-deep .description .cell .inner-cell div {
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  /*! autoprefixer: off */
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 4;
}

</style>

<script>
import _ from 'lodash'
import path from 'path'
import { mapGetters, mapState } from 'vuex'

import common from '@/utils/common'
import * as config from '@/config'
import * as query from '@/queries/product'
import * as define from '@/define'

import x from '@/components/mixins/mixin'
import { ELEMENT_TYPE } from '@/components/molecules/BaseCell'
import BaseListPage from '@/components/organisms/BaseListPage'

import BaseDisplayListModal from '@/components/organisms/BaseDisplayListModal'
import BaseDisplaySaveModal from '@/components/organisms/BaseDisplaySaveModal'
import BaseDisplaySelectModal from '@/components/organisms/BaseDisplaySelectModal'
import ToDownload from '@/components/organisms/ToDownload'

/**
 * 商品一覧画面 アクションモード
 */
export const ACTION_MODE = Object.freeze(
  {
    NORMAL: 0,         // 通常
    NEW_DATA: 1,       // 新規登録
    DISPLAY: 2         // 表示項目
  }
)

export default {
  name: 'ProductList',
  components: {
    BaseListPage,
    BaseDisplayListModal,
    BaseDisplaySaveModal,
    BaseDisplaySelectModal,
    ToDownload
  },
  mixins: [x],
  provide () {
    return {
      parentValidator: this.$validator
    }
  },
  data () {
    return {
      mode: ACTION_MODE.NORMAL,
      isDisplayListDialog: false,
      isDisplaySelectDialog: false,
      isDisplaySaveDialog: false
    }
  },
  computed: {
    define: () => define,
    ACTION_MODE: () => ACTION_MODE,
    ...mapGetters({
      getSortedList: 'sortedList'
    }),
    ...mapGetters('LocalStorage', {
      sortCondition: 'sortCondition'
    }),
    ...mapState({
      listIndex: (state) => state.Product.listIndex,
      tableSetList: (state) => state.Product.tableSetList,
      searchKeyword: (state) => state.Product.searchKeyword || '',
      isAndSearch: (state) => state.Product.isAndSearch || false,
      conditions: (state) => state.Product.searchConditions || {},
      tdLists: (state) => state.Product.tdLists,
      displayItems: (state) => state.Product.displayItems
    }),
    name () {
      return 'productList'
    },
    storePath () {
      return 'Product'
    },
    responsePath () {
      return 'data.products.edges'
    },
    baseQuery () {
      return query.GRAPH_QL_QUERY_PRODUCT_LIST
    },
    saveKey () {
      return 'productCondition'
    },
    linkKey () {
      return 'node.uuid'
    },
    tableDataList () {
      return this.sortedList
    },
    sortedList () {
      return this.getSortedList(this.storePath, this.sortCondition(this.name))
    },
    _tableSetList () {
      const returnValue = [
        {
          itemKey: 'image', isAvailable: false, isActive: false,
          label: '商品画像', path: 'imageData', imageDataGetAsync: this.loadImage, elementType: ELEMENT_TYPE.IMAGE, width: 150,
          imageWidth: '100px', imageHeight: '100px', asyncGetImage: true
        },
        {
          itemKey: 'productCategory', isAvailable: false, isActive: false,
          label: '商品区分', path: 'node.productCategory',
          elementType: ELEMENT_TYPE.SELECTBOX, width: 120, codeName: 'm_product.product_category'
        },
        {
          itemKey: 'code', isAvailable: false, isActive: false,
          label: '品番コード', path: 'node.code', elementType: ELEMENT_TYPE.TEXT, width: 180, sortable: 'custom'
        },
        {
          itemKey: 'name', isAvailable: false, isActive: false,
          label: '商品名', path: 'node.name', elementType: ELEMENT_TYPE.TEXT, sortable: 'custom'
        },
        {
          itemKey: 'productDescription', isAvailable: false, isActive: false,
          label: '商品説明', path: 'node.productDescription', elementType: ELEMENT_TYPE.TEXT, sortable: 'custom',
          class: 'description'
        }
      ]

      // 属性情報の追加
      for (const displayItem of (this.displayItems || []).filter((data) => data.isAttribute)) {
        returnValue.push(
          {
            itemKey: displayItem.itemKey, elementType: ELEMENT_TYPE.FUNCTION,
            type: 'String', label: displayItem.attributeDisplayName,
            path: (row) => {
              const attribute = _.get(row, `node.attributes`, [])
              const returnValue = attribute.find((data) => data.attrId === displayItem.attributeProperty)
              return (returnValue || {}).attrValue || ''
            },
            isAvailable: true, isActive: displayItem.isDisplay,
            displayOrder: displayItem.displayOrder
          }
        )
      }

      for (const displayItem of (this.displayItems || []).filter((data) => !data.isAttribute)) {
        const item = returnValue.find((data) => data.itemKey === displayItem.itemKey)
        if (item) {
          _.set(item, 'isAvailable', true)
          _.set(item, 'isActive', displayItem.isDisplay)
          _.set(item, 'displayOrder', displayItem.displayOrder)
        }
      }

      return returnValue.sort((a, b) => a.displayOrder < b.displayOrder ? -1 : 1)
    }
  },
  watch: {
    displayItems: {
      handler: function (val) {
        this.$store.dispatch({
          type: 'set',
          states: {
            'Product/tableSetList': this._tableSetList
          }
        })
      },
      deep: true
    }
  },
  async created () {
    try {
      this.$loadingService.show()
      // 設定tableSetListの情報取得
      const work = await common.getDisplayItems('product_list')

      this.$store.dispatch({
        type: 'set',
        states: {
          'Product/displayItems': work
        }
      })

      this.$store.dispatch({
        type: 'set',
        states: {
          'Product/tableSetList': this._tableSetList
        }
      })
    } catch (error) {
      this.$notify.error({ error: error })
      throw error
    } finally {
      this.$loadingService.close()
    }
  },
  methods: {
    async loadImage (row) {
      // sortedListは最新ではないので正しいデータを取得
      row = this.tdLists.find((elements) => elements.node.uuid === row.node.uuid)
      if (row.ImageNone) {
        return require('@/assets/img/no-image.png')
      } else {
        if (row.imageSignedUrl) {
          // fetchでアクセス.
          const res = await fetch(row.imageSignedUrl)
          if (res.ok) {
            // Blob形式でデータ取得.
            const blob = await res.blob()
            const returnUrl = (window.URL || window.webkitURL).createObjectURL(blob)
            return returnUrl
          }
        }
        const url = new URL(path.join(config.PRODUCT_IMAGE_URL, encodeURIComponent(row.node.code)))
        const method = 'get'
        const response = await common.apiAxios(url, { method })
        let imageSignedUrl = null
        if (response.data.noImage) {
          // 署名付きURLである場合、期限が切れるので再取得させるため更新しない
          this.tdLists.find((elements) => elements.node.uuid === row.node.uuid).ImageNone = true
          this.$store.dispatch({
            type: 'set',
            states: {
              'Product/tdLists': this.tdLists
            }
          })
          return require('@/assets/img/no-image.png')
        } else {
          imageSignedUrl = response.data.imageSignedUrl
        }
        // 署名付きURLの保存
        this.tdLists.find((elements) => elements.node.uuid === row.node.uuid).imageSignedUrl = imageSignedUrl
        this.$store.dispatch({
          type: 'set',
          states: {
            'Product/tdLists': this.tdLists
          }
        })

        // fetchでアクセス.
        const res = await fetch(imageSignedUrl)
        // Blob形式でデータ取得.
        const blob = await res.blob()
        const returnUrl = (window.URL || window.webkitURL).createObjectURL(blob)
        return returnUrl
      }
    },
    createCategoryText (row) {
      return _.join(row.category, ', ')
    },
    onRowClick (row, column, event) {
      const id = _.get(row, this.linkKey)
      const index = this.$_mixin_findIndex(this.sortedList, { [this.linkKey]: id })
      this.$_mixin_onLinkClick(index, this.storePath)
      this.$router.push({ path: `/product/${row.node.uuid}` })
    },
    /**
     * アクションボタン
     * @param {Number} mode モード
     */
    actionCommand: async function (mode) {
      switch (mode) {
        case ACTION_MODE.NEW_DATA:
          this.createProduct()
          break
        case ACTION_MODE.DISPLAY:
          // 表示項目設定ダイアログ表示
          this.isDisplayListDialog = true
          break
        default:
          break
      }
    },
    dataConvert (result) {
      // 不要なattributeは除去する
      const needAttribute = (this.displayItems || [])
        .filter((data) => data.isAttribute)
        .map((data) => data.attributeProperty)

      result.forEach((data) => {
        _.set(data, 'node.attributes',
          _.get(data, 'node.attributes', []).filter((data) => {
            return needAttribute.includes(data.attrId)
          }))
      })
    },
    createProduct () {
      this.$_mixin_onLinkClick(this.listIndex, this.storePath, define.MODE.NEW)
      this.$router.push({ path: 'product/0' })
    },
    clickDisplaySave () {
      this.isDisplaySaveDialog = true
    },
    clickDisplaySelect () {
      this.isDisplaySelectDialog = true
    }
  }
}
</script>
